import { KMenuButton, KTagWithIcon, KText } from '@/ui-library/components';
import {
  Avatar,
  Box,
  Center,
  Flex,
  Icon,
  Link,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import { Recording01 } from '@kula-ai/icons/solid/Recording01';
import { type InterviewType } from '@/types/interview';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { JoinInterviewButton } from './JoinInterviewButton';
import { getInterviewTime, locationIconMap } from '../utils';
import { SlashDivider } from '@kula-ai/icons/outline/SlashDivider';
import { Stars02 } from '@kula-ai/icons/dual-tone/Stars02';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { XClose } from '@kula-ai/icons/dual-tone/XClose';
// import { SendMessageTrigger } from '@/pages/interviews-revamp/component/SendMessageTrigger';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { InterviewIntelligenceStatusTag } from '@/components/shared/interviews-revamp/component/InterviewIntelligenceStatusTag';
import { InterviewStatusTag } from '@/components/shared/interviews-revamp/component/InterviewStatusTag';
import { useCameFromPath } from '@/hooks/usePreviousLocation';
import { ModalWithForm } from '@/components/ats/modal/ModalWithForm';
import {
  useDeleteInterviewMutation,
  useRunDemoHarnessMutation,
} from '@/store/query/scheduling';
import { useDateFormat, useShowToast } from '@/hooks';
import { useEffect, useRef, useState } from 'react';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';
import { MeetingLinkDetails } from '@/components/shared/interviews-revamp/component/MeetingLinkDetails';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useKFlags } from '@/hooks/use-k-flags';
import { ScheduleInterview } from '@/components/shared/interview-manager/ScheduleInterview';
import { KDrawer } from '@/ui-library/components';
import { InterviewAssistantSlider } from './InterviewAssistantSlider';
import { DemoInterviewModal } from './DemoInterviewModal';
import { useQuery } from '@tanstack/react-query';
import { API_SERVICE } from '@/api/candidate-job-slider';
import {
  APPLICATION_QUERY_STALE_MS,
  getApplicationQueryKey,
} from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
dayjs.extend(relativeTime);

interface InterviewHeaderProps {
  interview: InterviewType;
  refetch: () => void;
  interviewState: any;
}

export const InterviewHeader = ({
  interview,
  refetch,
  interviewState,
}: InterviewHeaderProps) => {
  const history = useHistory();
  const toast = useShowToast();
  const cameFromInterviews = useCameFromPath('/interviews');
  const {
    meeting_link,
    candidate,
    transfer_info,
    intelligence_status,
    prep_available,
    interviewer_assistant_accessible,
    interviewer_assistant_enabled,
    interview_intelligence_session_id,
    meeting_status,
    ats_job,
    ats_application_id,
    ats_job_stage_id,
    ats_job_stage_action_id = null,
    can_edit,
    start_time,
    end_time,
    ats_job_stage_name,
  } = interview;

  const { pInterviewerAssistant, tDemoInterviewHarness } = useKFlags() as {
    pInterviewerAssistant?: boolean;
    tDemoInterviewHarness?: boolean;
  };
  const currUser = useSelector(selectCurrUser);
  // Managing or scheduling a round is deliberately insufficient: every Assistant phase is private to
  // active interviewer attendees. Prefer the backend's authoritative, user-scoped access decision; the
  // participant fallback keeps the UI safe during a rolling deploy with older API responses.
  const isCurrentUserInterviewer =
    interviewer_assistant_accessible ??
    !!interview.participants?.some(
      (participant) => participant.id === currUser?.id
    );

  const {
    isBeforeInterview,
    isAfterInterview,
    isDuringInterview,
    interviewInfoActions,
    hideInterviewInfoInToolbar,
  } = interviewState;

  const isNoteTaker = intelligence_status !== 'pending';
  const isCancelledInterview = meeting_status === 'cancelled';

  const [deleteInterview, { isLoading: isCancelling }] =
    useDeleteInterviewMutation();
  const editInterviewActions = useDisclosure();
  const cancelInterviewActions = useDisclosure();
  // Demo harness — gated on the flag (the run spawns real bots and burns spend) and a Meet link for the
  // bots to dial into.
  // The DEMO pill is tracked in sessionStorage so it survives a refresh while the short simulated round
  // runs; it only shows in the live view.
  const demoHarnessAvailable =
    isCurrentUserInterviewer && !!tDemoInterviewHarness && !!meeting_link;
  const demoActions = useDisclosure();
  const [runDemoHarness, { isLoading: isStartingDemo }] =
    useRunDemoHarnessMutation();
  const [demoRunning, setDemoRunning] = useState(
    () => sessionStorage.getItem(`demoHarness:${interview.id}`) === '1'
  );
  // One entrypoint, one surface across the round's lifecycle: Prep before, Live Coach during/just
  // after, Coach once the transcript is processed.
  const assistantActions = useDisclosure();
  const [assistantMode, setAssistantMode] = useState<'prep' | 'live' | 'coach'>(
    'prep'
  );
  const [assistantCopilotDefault, setAssistantCopilotDefault] = useState(false);
  // Once the round has been LIVE in this page session, keep Live Coach available even after it ends (all
  // three phases stay navigable). A fresh load of an already-ended round never sets this, so it falls back
  // to Prep + Coach and lands on Coach — matching today's post-interview behaviour.
  const [wasLiveThisSession, setWasLiveThisSession] = useState(false);

  // Phase availability:
  // - Prep is available whenever a guide exists.
  // - Live Coach is offered whenever the assistant is on for this round; it unlocks once the round is
  //   under way (during) and stays available right after, until the retrospective Coach note is ready.
  // - Coach (the retrospective note) is ready once the round is over and its recording is
  //   processing/done. Keyed on the intelligence_status flip — which only reaches processing/completed
  //   AFTER the call actually ends (it's `recording` while live) — NOT the scheduled end_time. A call that
  //   ends early must surface the handoff right away, not wait out the clock (that lag was a real bug).
  // The button shows if there's anything to open and opens on whichever phase is the natural one. The
  // in-surface Phase switch flips between them; later phases are locked until ready.
  const coachReady =
    isCurrentUserInterviewer &&
    !!pInterviewerAssistant &&
    ['processing', 'completed'].includes(intelligence_status) &&
    !!interviewer_assistant_enabled;
  // Live Coach needs a registered interview-intelligence session to stream from (created at bot
  // dispatch, soft-deleted on removal) — without it there's nothing to connect to.
  const liveReady =
    isCurrentUserInterviewer &&
    !!pInterviewerAssistant &&
    !!interviewer_assistant_enabled &&
    !!interview_intelligence_session_id &&
    // A running demo streams the coach regardless of the scheduled window — the harness dispatches the bot +
    // registers the interview-intelligence session immediately, so an UPCOMING (or completed) interview can still show Live Coach.
    (demoRunning ||
      isDuringInterview ||
      (isAfterInterview && (!coachReady || wasLiveThisSession)));
  const assistantPrepEnabled =
    isCurrentUserInterviewer && !!pInterviewerAssistant && !!prep_available;
  const assistantLiveEnabled =
    isCurrentUserInterviewer &&
    !!pInterviewerAssistant &&
    !!interviewer_assistant_enabled &&
    // Hide the Live Coach phase entirely on a fresh load of an already-ended round (coach ready, never live in
    // this page session) — Prep + Coach only, not a locked tab. When the round WAS live this session it stays
    // visible (all three navigable).
    !(coachReady && !wasLiveThisSession);
  const assistantCoachEnabled =
    isCurrentUserInterviewer && !!pInterviewerAssistant;
  const assistantDefaultMode: 'prep' | 'live' | 'coach' =
    coachReady && !wasLiveThisSession ? 'coach' : liveReady ? 'live' : 'prep';
  const showAssistant = assistantPrepEnabled || liveReady || coachReady;

  // Latch "was live this session" the first time the Live Coach is available in this page session (the round
  // is underway, or just ended before the coach is ready). Keyed on liveReady, NOT isDuringInterview, so it
  // also fires for a session run outside the scheduled window (the harness, or a late/early call).
  useEffect(() => {
    if (liveReady) setWasLiveThisSession(true);
  }, [liveReady]);

  // While the Live Coach is up but the coaching note isn't ready yet, poll the interview so its
  // intelligence_status flips (recording → processing → completed) the moment the call ends — that's what
  // surfaces the handoff strip and unlocks the Coach tab IN PLACE, no manual refresh. Stops once ready.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (!liveReady || coachReady) return;
    const id = setInterval(() => refetchRef.current?.(), 12_000);
    return () => clearInterval(id);
  }, [liveReady, coachReady]);

  // The RECORDING lands AFTER the note: Recall finalizes the video a few minutes past call-end, so
  // `recording_url` shows up later than `coachReady` (and later still for a demo, whose note is generated
  // eagerly). The poll above stops the instant coachReady flips, which left the coach note's timestamp chips
  // dead — they only become clickable once a recording exists — until a manual page reload. Keep polling until
  // the URL lands so the chips go live IN PLACE. Capped so a failed/purged recording doesn't poll forever.
  const recordingUrl = (interview as any)?.recording_url;
  useEffect(() => {
    if (!coachReady || recordingUrl) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      refetchRef.current?.();
      if (attempts >= 25) clearInterval(id); // ~5 min at 12s
    }, 12_000);
    return () => clearInterval(id);
  }, [coachReady, recordingUrl]);

  // After a demo starts, the worker flips the interview to LIVE and registers the interview-intelligence session a beat after the
  // POST returns. Poll until Live Coach can connect (liveReady) so the surface switches on its own — no manual
  // refresh. Capped so a failed start doesn't poll forever.
  useEffect(() => {
    if (!demoRunning || liveReady) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      refetchRef.current?.();
      if (attempts >= 12) clearInterval(id);
    }, 2500);
    return () => clearInterval(id);
  }, [demoRunning, liveReady]);

  const openAssistant = (mode: 'prep' | 'live' | 'coach', copilot = false) => {
    setAssistantMode(mode);
    setAssistantCopilotDefault(copilot);
    assistantActions.onOpen();
  };

  // Deep link: `/interviews/:id/prep|live|coach` (from email/Slack) lands the interviewer straight in
  // the assistant, in the right mode; an optional `?copilot=1` opens the chat by default. The mode
  // segment is stripped back to the base path after opening so refresh/back stay clean (same route
  // pattern, so this doesn't remount the page).
  const location = useLocation();
  const { interviewId, assistantMode: deepLinkMode } = useParams<{
    interviewId: string;
    assistantMode?: 'prep' | 'live' | 'coach';
  }>();
  useEffect(() => {
    if (!deepLinkMode) return;
    if (!pInterviewerAssistant || !isCurrentUserInterviewer) {
      history.replace(`/interviews/${interviewId}`);
      toast({
        id: 'interviewer-assistant-access-denied',
        title: 'This page is private to the interview panel.',
        status: 'error',
      });
      return;
    }

    const wantsCopilot =
      new URLSearchParams(location.search).get('copilot') === '1';
    openAssistant(deepLinkMode, wantsCopilot);
    history.replace(`/interviews/${interviewId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pInterviewerAssistant, isCurrentUserInterviewer, deepLinkMode]);
  const rightSectionRef = useRef<HTMLDivElement>(null);
  const intelligenceStatusRef = useRef<HTMLDivElement>(null);
  const interviewStatusRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(dayjs());
    }, 10000); // Update every 10 seconds for accurate countdown
    return () => clearInterval(timer);
  }, []);

  const availableWidth =
    (headerRef.current?.clientWidth || 1000) -
    (intelligenceStatusRef.current?.clientWidth || 100) -
    (interviewStatusRef.current?.clientWidth || 100) -
    (rightSectionRef.current?.clientWidth || 100) -
    150;

  const handleBreadcrumbClick = () => {
    const hasPreviousHistory = history.length > 1;

    if (cameFromInterviews && hasPreviousHistory) {
      // If user came from interviews section and has history, go back
      history.goBack();
    } else {
      // If user came from outside interviews or has no history, go to upcoming
      history.push('/interviews/upcoming');
    }
  };

  const { dateFormat, timeFormat } = useDateFormat();
  const interviewTimeResult = getInterviewTime({
    startTime: start_time,
    endTime: end_time,
    timeFormat: timeFormat,
    dateFormat: dateFormat.label,
    currUserTimezone: currUser?.time_zone,
  });

  const menuItems = [
    hideInterviewInfoInToolbar && {
      id: 'interview_info',
      label: 'Interview info',
      icon: InfoCircle,
      onClick: interviewInfoActions?.onOpen,
    },
    can_edit && {
      id: 'edit_interview',
      label: 'Edit interview',
      icon: Edit05,
      onClick: () => {
        editInterviewActions.onOpen();
      },
    },
    !isCancelledInterview &&
      can_edit && {
        id: 'cancel_interview',
        label: 'Cancel interview',
        icon: XClose,
        onClick: () => {
          cancelInterviewActions.onOpen();
        },
      },
    demoHarnessAvailable && {
      id: 'run_demo_interview',
      label: 'Run demo interview',
      icon: Recording01,
      onClick: () => demoActions.onOpen(),
    },
  ].filter(Boolean);

  const handleStartDemo = async (durationMin: number) => {
    const result = (await runDemoHarness({
      interviewId: interview.id,
      durationMin,
    })) as { error?: unknown };
    if (result?.error) {
      toast({
        title: 'Could not start the demo interview',
        status: 'error',
      });
      return;
    }
    demoActions.onClose();
    sessionStorage.setItem(`demoHarness:${interview.id}`, '1');
    setDemoRunning(true);
    toast({
      title: 'Demo interview started',
      description:
        'Join the Meet and admit the 3 Kula bots — the Live Coach lights up when they start talking.',
      status: 'success',
      duration: 8000,
    });
  };

  const handleCancelInterview = async () => {
    const result = (await deleteInterview({ interviewId: interview.id })) as {
      error: [];
    };
    if (result?.error?.length) {
      toast({ title: 'Cancelling the interview failed', status: 'error' });
    } else {
      toast({ title: 'The interview has been canceled.' });
      const hasHistoryToGoBack = history.length > 1;
      if (cameFromInterviews && hasHistoryToGoBack) {
        history.goBack();
      } else {
        history.push('/interviews/upcoming');
      }
    }
  };

  // The new wizard derives the candidate phone from `applicationData.contact`
  // (`phone_code` + `phone_number`). The interview-detail response omits those
  // structured fields, so without this fetch the Candidate phone field renders
  // empty when Location=Phone Interview, even though the contact has one on
  // file. Shared cache key dedupes with the slider/list prefetch.
  const applicationIdStr =
    ats_application_id !== null && ats_application_id !== undefined
      ? String(ats_application_id)
      : null;
  const { data: applicationData } = useQuery<Record<string, unknown>>({
    queryKey: getApplicationQueryKey(applicationIdStr),
    queryFn: () =>
      API_SERVICE.getApplication(applicationIdStr as string, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
    enabled: editInterviewActions.isOpen && !!applicationIdStr,
    refetchOnWindowFocus: false,
  });

  const renderModals = () => {
    return (
      <>
        {editInterviewActions.isOpen && (
          <KDrawer
            isOpen={true}
            onClose={editInterviewActions?.onClose}
            handleOutsideClose={() => {}}
            variant="full"
            contentProps={{ maxW: '90%' }}
            skipHeader
            skipFooter
          >
            <ScheduleInterview
              mode="manual"
              apiReference="edit"
              applicationId={ats_application_id}
              applicationData={applicationData}
              jobId={ats_job?.id}
              stageId={ats_job_stage_id}
              stageActivityId={ats_job_stage_action_id}
              interviewId={interview.id}
              candidateId={candidate?.id}
              candidateName={candidate?.name}
              candidateAvatar={candidate?.avatar ?? undefined}
              candidateEmail={candidate?.email}
              jobTitle={ats_job?.name}
              stageName={ats_job_stage_name}
              onClose={editInterviewActions?.onClose}
              onSuccess={() => {
                refetch();
                editInterviewActions?.onClose();
              }}
            />
          </KDrawer>
        )}
        {cancelInterviewActions.isOpen && (
          <ModalWithForm
            withForm={false}
            isOpen={cancelInterviewActions?.isOpen}
            onClose={cancelInterviewActions?.onClose}
            headerText="Cancel interview?"
            subHeaderText="This will remove the interview from the calendars of the candidate and interviewers"
            primaryCtaText="Cancel interview"
            handleSubmit={handleCancelInterview}
            isCtaLoading={isCancelling}
          />
        )}
        {assistantActions.isOpen && (
          <InterviewAssistantSlider
            interview={interview}
            isOpen={assistantActions.isOpen}
            onClose={assistantActions.onClose}
            defaultMode={assistantMode}
            prepEnabled={assistantPrepEnabled}
            liveEnabled={assistantLiveEnabled}
            liveReady={liveReady}
            coachEnabled={assistantCoachEnabled}
            coachReady={coachReady}
            demo={demoRunning}
            defaultCopilotOpen={assistantCopilotDefault}
          />
        )}
        {demoActions.isOpen && (
          <DemoInterviewModal
            isOpen={demoActions.isOpen}
            onClose={demoActions.onClose}
            onStart={handleStartDemo}
            isStarting={isStartingDemo}
          />
        )}
      </>
    );
  };

  const rightHeaderMenu = () => {
    if (isCancelledInterview) return null;

    const showJoinInterviewButton =
      meeting_link &&
      (isBeforeInterview || isDuringInterview) &&
      !isAfterInterview;
    // const showSendMessageTrigger = isAfterInterview && !isDuringInterview;
    const showMoreActions = menuItems.length > 0;

    return (
      <Flex gap="12px" align="center" ref={rightSectionRef}>
        {showAssistant && (
          <Flex
            as="button"
            align="center"
            gap="7px"
            px="14px"
            h="36px"
            borderRadius="8px"
            color="base-white"
            bgGradient="linear(135deg, primary-500, primary-600)"
            boxShadow="0 8px 20px -8px rgba(96, 90, 251, 0.55)"
            _hover={{
              bgGradient: 'linear(135deg, primary-600, primary-700)',
              transform: 'translateY(-1px)',
            }}
            transition="0.18s"
            onClick={() => openAssistant(assistantDefaultMode)}
          >
            <Icon as={Stars02} w="16px" h="16px" color="base-white" />
            <KText
              size="sm-semi-bold"
              color="base-white"
              fontSize="13px"
              fontWeight="600"
            >
              Interviewer Assistant
            </KText>
          </Flex>
        )}
        {showJoinInterviewButton && (
          <JoinInterviewButton interview={interview} />
        )}
        {/* {showSendMessageTrigger && (
          <SendMessageTrigger interview={interview} isHeader />
        )} */}
        {showMoreActions && (
          <KMenuButton
            variant="tertiary"
            triggerContent="More"
            menuContent={menuItems.map((item) => (
              <MenuItem
                key={item.id}
                onClick={item.onClick}
                _hover={{ fontWeight: 'semibold', bg: 'secondary-50' }}
                gap="8px"
              >
                {item.icon && (
                  <Icon
                    as={item.icon}
                    width="16px"
                    height="16px"
                    color="secondary-600"
                  />
                )}
                <KText fontWeight="inherit" size="sm-regular">
                  {item.label}
                </KText>
              </MenuItem>
            ))}
          />
        )}
      </Flex>
    );
  };

  // breadcrumb on the right + more actions on the right (rightHeaderMenu)
  const firstLevel = () => {
    return (
      <Flex justifyContent="space-between" alignItems="flex-start" minH="32px">
        {/* left section */}
        <Flex alignItems="center" gap="6px">
          <Icon as={Recording01} color="secondary-600" />
          <KText
            variant="heading_3"
            color="secondary-600"
            cursor="pointer"
            onClick={handleBreadcrumbClick}
          >
            Interviews /
          </KText>
        </Flex>
        {rightHeaderMenu()}
      </Flex>
    );
  };

  const candidateLink = (() => {
    // is_current marks the candidate's live application (the active leaf of the chain, resolved
    // server-side), so this links straight to their current job for multi-hop transfers too.
    if (transfer_info?.history?.length && transfer_info?.applications) {
      const currentAppId = transfer_info.history.find(
        (appId) => transfer_info.applications[appId]?.is_current
      );
      if (currentAppId) {
        const currentApp = transfer_info.applications[currentAppId];
        const jobId = currentApp?.application?.job?.id;
        return `/jobs/${jobId}?tab=candidates&appId=${currentAppId}`;
      }
    }
    return `/jobs/${ats_job?.id}?tab=candidates&appId=${ats_application_id}`;
  })();

  // user info and status tags
  const secondLevel = () => {
    return (
      <Flex alignItems="center" gap="8px">
        <Avatar
          src={candidate.avatar}
          name={candidate.name}
          width="24px"
          height="24px"
          className="avatar-sm"
          borderRadius="6px"
        />
        <Link href={candidateLink} target="_blank" outline="none" _focus={{}}>
          <TruncatedTextWithTooltip
            text={candidate.name}
            variant="heading_1"
            maxW={`${availableWidth * 0.5}px`}
          />
        </Link>
        {interview?.transfer_info && (
          <TransferredBadge
            transferInfo={interview?.transfer_info}
            showTooltipToLabel={true}
          />
        )}
        <Icon as={SlashDivider} color="secondary-600" h="20px" />
        <TruncatedTextWithTooltip
          text={interview.name}
          variant="heading_1"
          maxW={`${availableWidth * 0.5}px`}
        />

        <Box ref={intelligenceStatusRef}>
          {isNoteTaker && isBeforeInterview && (
            <KTagWithIcon
              value="AI notetaker enabled"
              icon={Stars02}
              iconProps={{ color: 'pastel-3-dark' }}
              bg="pastel-3-light"
              color="pastel-3-dark"
              whiteSpace="nowrap"
            />
          )}
          {!isBeforeInterview && isNoteTaker && (
            <InterviewIntelligenceStatusTag status={intelligence_status} />
          )}
        </Box>
        <Box ref={interviewStatusRef}>
          <InterviewStatusTag interview={interview} />
        </Box>
        {isBeforeInterview &&
          !isCancelledInterview &&
          dayjs(interview.start_time).diff(now, 'minutes') < 30 &&
          (() => {
            const diffInSeconds = dayjs(interview.start_time).diff(
              now,
              'seconds'
            );
            const mins = Math.ceil(diffInSeconds / 60);
            let label = `Starts in ${mins} minutes`;
            if (diffInSeconds < 3) {
              return null;
            } else if (diffInSeconds < 30) {
              label = 'Starts in a few seconds';
            } else if (diffInSeconds < 60) {
              label = 'Starts in a minute';
            }
            return (
              <KTagWithIcon value={label} icon={Clock} whiteSpace="nowrap" />
            );
          })()}
      </Flex>
    );
  };

  // interview info
  const thirdLevel = () => {
    const { meeting_meta, location } = interview || {};

    const interviewTimeLabel =
      typeof interviewTimeResult === 'string'
        ? interviewTimeResult
        : interviewTimeResult.interviewTimeLabel;

    const videoDetails =
      Array.isArray(meeting_meta) &&
      meeting_meta?.find((item) => item?.entry_point_type === 'video');

    const locationIcon = locationIconMap[location];

    return (
      <Flex pt="12px" alignItems="center">
        {/* job */}
        <Flex alignItems="center" gap="6px">
          <Icon as={LayersThree02} color="secondary-500" />
          <Link
            href={`/jobs/${ats_job?.id}`}
            target="_blank"
            outline="none"
            width="fit-content"
            _focus={{}}
          >
            <KText variant="paragraph" fontWeight="550">
              {ats_job?.name}
            </KText>
          </Link>
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="center"
          p="0 12px"
          color="secondary-900"
          borderRadius="50%"
          fontSize="12px"
          lineHeight="1"
        >
          •
        </Flex>

        {/* location */}
        <Flex alignItems="center" gap="6px">
          {locationIcon?.icon && (
            <Icon as={locationIcon?.icon} color={locationIcon?.color} />
          )}
          <KText
            textTransform="capitalize"
            size="sm-medium"
            color="secondary-700"
          >
            {location?.replace('_', ' ')}
          </KText>
          {videoDetails && (
            <KPopover
              triggerElement={
                <Center>
                  <Icon as={InfoCircle} cursor="pointer" />
                </Center>
              }
              popoverProps={{ trigger: 'hover' }}
              showCloseButton={false}
            >
              <MeetingLinkDetails
                videoDetails={videoDetails}
                location={location}
              />
            </KPopover>
          )}
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="center"
          p="0 12px"
          color="secondary-900"
          borderRadius="50%"
          fontSize="12px"
          lineHeight="1"
        >
          •
        </Flex>

        {/* interview time */}
        <Flex alignItems="center" gap="6px">
          <Icon as={Clock} color="secondary-500" />
          <KText variant="paragraph" fontWeight="550">
            {interviewTimeLabel}
          </KText>
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Flex
        p="12px 24px"
        borderBottom="1px solid"
        borderColor="secondary-100"
        boxShadow="2px 4px 10px 0px #D7DAEB1A,8px 17px 19px 0px #D7DAEB17,18px 38px 25px 0px #D7DAEB0D,32px 68px 30px 0px #D7DAEB03,50px 107px 33px 0px #D7DAEB00"
        flexDir="column"
        gap="6px"
        ref={headerRef}
      >
        {firstLevel()}
        {secondLevel()}
        {thirdLevel()}
      </Flex>
      {renderModals()}
    </>
  );
};
